
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Monitor, Target, Calendar, CheckCircle2, MessageSquare, Compass, ShieldCheck, Heart, AlertCircle, Sparkles, ArrowRight } from 'lucide-react';
import { Language, View } from '../types';

interface ProcessPageProps {
  lang: Language;
  setView: (view: View) => void;
  onBack: () => void;
}

const ProcessPage: React.FC<ProcessPageProps> = ({ lang, setView, onBack }) => {
  const gradientTextClasses = "bg-gradient-to-r from-primary via-teal-400 to-emerald-500 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]";
  const gradientBgClasses = "bg-gradient-to-r from-primary via-teal-400 to-emerald-500 animate-gradient bg-[length:200%_auto]";

  const content = {
    title: { en: "The Coaching Process", fa: "فرایند دوره‌ی کوچینگ" },
    subtitle: { 
        en: "Not just a conversation. A structured framework for measurable change.", 
        fa: "این یک گفت‌وگوی معمولی نیست. یک فرایند ساختارمند و پرقدرته که برای ایجاد تغییرهای قابل اندازه‌گیری طراحی شده." 
    },
    intro: {
        en: "In a coaching course, we partner to pursue a common goal: advancing your personal growth as we've designed it.",
        fa: "در یک دوره‌ی کوچینگ، من و تو در واقع با هم شریک می‌شیم که یک هدف مشترک رو دنبال کنیم. و این هدف مشترک، پیشرفتِ جنبه‌ای از رشدِ توعه که طراحی کردیم."
    },
    framework: {
        title: { en: "Coaching Framework", fa: "چارچوب کوچینگ" },
        text: { 
            en: "Coaching happens within a specific framework designed for your best results. Structure creates safety and measurability.",
            fa: "کوچینگ توی یک چارچوب مشخص انجام می‌شه. این چارچوب رو با هم طراحی می‌کنیم طوری که برای تو بهترین نتیجه رو بده. ساختار، ایمنی می‌سازه و کارمون رو سنجش‌پذیر می‌کنه."
        },
        items: [
            { icon: Clock, text: { en: "30-60 minute sessions (weekly/bi-weekly)", fa: "جلسات ۳۰ تا ۶۰ دقیقه‌ای (هفتگی یا دوهفته‌ای)" } },
            { icon: Monitor, text: { en: "Online, private, and confidential", fa: "آنلاین، خصوصی و محرمانه" } },
            { icon: Target, text: { en: "Long-term paths (3, 6, or 12 months recommended)", fa: "مسیرهای بلندمدت (۳، ۶ یا ۱۲ ماه معمولاً توصیه می‌شه)" } }
        ],
        footer: {
            en: "Transformation isn't linear. The ICF suggests at least 3 months for lasting impact. We design the path based on your goals and life context.",
            fa: "زمان‌بندی یکسانی برای همه‌ی افراد وجود نداره. تحول خطی نیست. پیشنهاد فدراسیون کوچینگ اینه که یک دوره‌ی کوچینگ حداقل سه ماهه باشه تا اثرات ماندگاری روی زندگیمون بذاره. مسیر رو بر اساس اهداف، زمینه زندگی و میزان آمادگی تو طراحی می‌کنیم."
        }
    },
    architecture: {
        title: { en: "Session Architecture", fa: "معماری جلسه" },
        steps: [
            { id: 1, title: { en: "Starting point", fa: "۱) شروع از وضعیت فعلی" }, desc: { en: "Focus on what's active and alive right now.", fa: "جلسه رو با چیزی که همین الان زنده و فعاله و موضوعی که می‌خوای روش کار کنی شروع می‌کنیم." } },
            { id: 2, title: { en: "Below the surface", fa: "۲) کاوش زیر سطح" }, desc: { en: "Illuminate patterns, structures, beliefs, and inner conflicts.", fa: "با سوال‌های دقیق، الگوها، ساختارها، باورها، احساسات و تضادهای درونی روشن می‌شن." } },
            { id: 3, title: { en: "Perspective shift", fa: "۳) تغییر زاویه دید و آگاهی" }, desc: { en: "New frameworks and insights take shape.", fa: "چارچوب‌ها و معناهای جدید دیده میشه و بینش‌های جدید شکل می‌گیره." } },
            { id: 4, title: { en: "Decision & Action", fa: "۴) تصمیم‌ها و اقدام" }, desc: { en: "Leave with a concrete step rooted in your new awareness.", fa: "جلسه رو با یک تصمیم یا اقدام مشخص که از دل آگاهی جدیدت شکل گرفته ترک می‌کنی." } }
        ]
    },
    between: {
        title: { en: "Between Sessions", fa: "بین جلسات" },
        quote: { en: "Where real transformation happens", fa: "«جایی که تحول واقعی زندگی اتفاق می‌افته»" },
        items: [
            { en: "Observe inner patterns in real-time", fa: "الگوهای درونی رو توی لحظه مشاهده کنی" },
            { en: "Conduct behavioral or relational experiments", fa: "آزمایش‌های رفتاری یا رابطه‌ای جدید انجام بدی" },
            { en: "Journal or track your inner system's signals", fa: "ژورنال‌نویسی، تأمل یا ردیابی سیگنال‌های سیستم درونی‌ت رو انجام بدی" },
            { en: "Collect data on what works and what resists change", fa: "داده جمع کنی درباره این‌که چی کار می‌کنه و چی در برابر تغییر مقاومت می‌کنه" }
        ]
    },
    accountability: {
        title: { en: "Accountability Structure", fa: "ساختار مسئولیت‌پذیری" },
        intro: { en: "I am a mirror, not a manager. My role is honesty and providing a safe container.", fa: "نقش من کنترل یا فشار نیست. نقش من ایجاد آینه، ساختار و یک چارچوب تعهده." },
        roles: [
            { en: "Strategic and deep questioning", fa: "صداقت و سؤال‌های رُک و عمیق" },
            { en: "Non-judgmental safe space for exploration", fa: "ایجاد فضای امن برای کاوش عمیق درونی با همدلی و بدون قضاوت بودن" },
            { en: "Reflection and feedback", fa: "بازتاب و فیدبک دادن بهت" },
            { en: "Companion in your self-discovery", fa: "همراهی در اکتشاف تو برای فهم خودت" },
            { en: "Support through tough transitions", fa: "حمایت توی گذارهای درونی سخت" },
            { en: "Tracking commitments and practices", fa: "پیگیری تعهدها و تمرین‌ها" }
        ]
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 bg-white selection:bg-teal-500/20">
      <div className="max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <button 
            onClick={onBack}
            className={`flex items-center gap-2 ${gradientTextClasses} font-black uppercase text-xs tracking-widest hover:translate-x-[-4px] transition-transform mb-8`}
          >
            <ArrowLeft size={16} className="text-teal-500" />
            {lang === 'en' ? "Back" : "بازگشت"}
          </button>
          
          <h1 className="hero-heading font-black text-brandDark uppercase leading-none mb-6">
            {content.title[lang]}
          </h1>
          <p className="text-xl md:text-2xl font-black text-gray-500 uppercase tracking-widest mb-10 leading-tight">
            {content.subtitle[lang]}
          </p>
          <div className="p-8 bg-gray-50 border-l-4 rtl:border-l-0 rtl:border-r-4 border-teal-500 rounded-sm">
            <p className="text-lg md:text-xl font-medium text-gray-600 leading-relaxed italic">
              {content.intro[lang]}
            </p>
          </div>
        </motion.div>

        {/* Framework */}
        <section className="mb-24">
            <div className="flex items-center gap-4 mb-8">
                <Compass className="text-teal-500 w-8 h-8" />
                <h2 className="text-2xl md:text-3xl font-black uppercase text-brandDark">
                    {content.framework.title[lang]}
                </h2>
            </div>
            <p className="text-gray-500 text-lg font-medium mb-10">
                {content.framework.text[lang]}
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-10">
                {content.framework.items.map((item, i) => (
                    <div key={i} className="p-6 border border-gray-100 rounded-sm bg-gray-50 flex flex-col items-start gap-4 shadow-sm hover:shadow-md transition-shadow">
                        <item.icon className="text-teal-500 w-6 h-6" />
                        <span className="text-brandDark font-bold text-sm leading-relaxed">{item.text[lang]}</span>
                    </div>
                ))}
            </div>
            <p className="text-gray-500 font-medium italic border-t border-gray-100 pt-6">
                {content.framework.footer[lang]}
            </p>
        </section>

        {/* Session Architecture */}
        <section className="mb-24">
            <h2 className="text-2xl md:text-3xl font-black uppercase text-brandDark mb-12 flex items-center gap-4">
                <Monitor className="text-teal-500 w-8 h-8" />
                {content.architecture.title[lang]}
            </h2>
            <div className="space-y-12 relative pl-6 rtl:pl-0 rtl:pr-6 border-l rtl:border-l-0 rtl:border-r border-gray-100">
                {content.architecture.steps.map((step, i) => (
                    <div key={i} className="relative">
                        <div className="absolute top-1.5 -left-[31px] rtl:-left-auto rtl:-right-[31px] w-4 h-4 rounded-full bg-white border-2 border-teal-500" />
                        <h3 className="text-xl font-black text-brandDark uppercase mb-2">{step.title[lang]}</h3>
                        <p className="text-gray-500 font-medium leading-relaxed">{step.desc[lang]}</p>
                    </div>
                ))}
            </div>
        </section>

        {/* Between Sessions */}
        <section className="mb-24 bg-brandDark text-white p-10 md:p-16 rounded-sm relative overflow-hidden">
             <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 blur-[80px] rounded-full" />
             <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-black uppercase mb-4">{content.between.title[lang]}</h2>
                <h3 className="text-teal-400 text-xl font-bold italic mb-10">{content.between.quote[lang]}</h3>
                <div className="grid md:grid-cols-2 gap-8">
                    {content.between.items.map((item, i) => (
                        <div key={i} className="flex gap-4 items-start">
                            <CheckCircle2 className="text-teal-500 w-5 h-5 shrink-0 mt-1" />
                            <p className="text-gray-300 font-medium leading-relaxed">{item[lang]}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-10 font-black uppercase tracking-widest text-sm text-teal-400 border-t border-white/10 pt-6">
                    {lang === 'en' ? "Return with lived data, not just theory." : "با داده‌های زیسته برمی‌گردی، نه فقط نظریه."}
                </p>
             </div>
        </section>

        {/* Accountability */}
        <section className="mb-24">
            <h2 className="text-2xl md:text-3xl font-black uppercase text-brandDark mb-8 flex items-center gap-4">
                <ShieldCheck className="text-teal-500 w-8 h-8" />
                {content.accountability.title[lang]}
            </h2>
            <p className="text-gray-500 text-lg font-medium mb-10">
                {content.accountability.intro[lang]}
            </p>
            <div className="grid md:grid-cols-2 gap-4">
                {content.accountability.roles.map((role, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-gray-50 rounded-sm border border-gray-100">
                        <CheckCircle2 className="text-teal-500 w-4 h-4 shrink-0" />
                        <span className="text-brandDark font-bold text-sm">{role[lang]}</span>
                    </div>
                ))}
            </div>
            <p className="mt-10 font-black text-brandDark italic">{lang === 'en' ? "The final decision is always yours." : "تصمیم نهایی همیشه با خودته."}</p>
        </section>

        {/* Investment & Ethics */}
        <section className="mb-24 grid md:grid-cols-2 gap-12">
            <div>
                <h2 className="text-xl font-black uppercase text-brandDark mb-6 flex items-center gap-3">
                    <Heart className="text-red-400 w-6 h-6" />
                    {lang === 'en' ? "Ethics & Scope" : "مرزهای اخلاقی و دامنه کار"}
                </h2>
                <p className="text-gray-500 font-medium leading-relaxed">
                    {lang === 'en' 
                        ? "Coaching is not psychotherapy. If clinical mental health issues arise, I may suggest a therapist. Confidentiality and psychological safety are the foundation." 
                        : "کوچینگ روان‌درمانی نیست. اگه تروما، PTSD یا مسائل سلامت روان مطرح باشه، ممکنه پیشنهاد کنم با یه درمانگر کار کنی. محرمانگی و ایمنی روان‌شناختی اصول پایه هستن."}
                </p>
            </div>
            <div>
                <h2 className="text-xl font-black uppercase text-brandDark mb-6 flex items-center gap-3">
                    <Sparkles className="text-teal-500 w-6 h-6" />
                    {lang === 'en' ? "What you invest in" : "دقیقاً روی چی سرمایه‌گذاری می‌کنی"}
                </h2>
                <ul className="space-y-3 text-gray-500 font-medium text-sm">
                    {[
                        { en: "A structured process for redesign", fa: "یک فرایند ساختاریافته برای بازطراحی سیستم درونی‌ت" },
                        { en: "A confidential and safe vessel", fa: "یک فضای محرمانه و امن برای کاوش عمیق" },
                        { en: "Strategic questions & frameworks", fa: "سؤال‌های استراتژیک و چارچوب‌هایی برای فهم خودت" },
                        { en: "Long-term partnership", fa: "یک شراکت متمرکز روی معماری زندگی تو، نه فقط هدف‌های کوتاه‌مدت" }
                    ].map((li, i) => <li key={i} className="flex gap-2">• {li[lang]}</li>)}
                </ul>
            </div>
        </section>

        {/* How to Start */}
        <section className="mb-24 p-10 md:p-16 border border-gray-100 rounded-sm text-center">
            <h2 className="text-3xl font-black uppercase text-brandDark mb-10">{lang === 'en' ? "How to Start" : "مسیر شروع"}</h2>
            <div className="grid md:grid-cols-3 gap-8 text-left rtl:text-right">
                <div>
                    <h4 className="font-black text-teal-500 mb-2">1. {lang === 'en' ? "Discovery" : "اکتشاف"}</h4>
                    <p className="text-sm text-gray-500 font-medium">{lang === 'en' ? "Honest chat to answer questions. (Free, 30-60m)" : "جلسه اکتشافی: یک گپ صادقانه برای جواب دادن به سؤال‌هات. (رایگان، ۳۰–۶۰ دقیقه)"}</p>
                </div>
                <div>
                    <h4 className="font-black text-teal-500 mb-2">2. {lang === 'en' ? "Decision" : "تصمیم"}</h4>
                    <p className="text-sm text-gray-500 font-medium">{lang === 'en' ? "Checking the fit. Are we right for each other? (Free, 30-60m)" : "جلسه تصمیم‌گیری: بررسی فیت بودن کوچ و کلاینت. (رایگان، ۳۰–۶۰ دقیقه)"}</p>
                </div>
                <div>
                    <h4 className="font-black text-teal-500 mb-2">3. {lang === 'en' ? "Launch" : "شروع"}</h4>
                    <p className="text-sm text-gray-500 font-medium">{lang === 'en' ? "Weekly or bi-weekly online sessions." : "شروع جلسات هفتگی یا دوهفته‌یک‌بار، آنلاین."}</p>
                </div>
            </div>
        </section>

        {/* CTA */}
        <section className="mb-20">
          <div className="bg-gray-50 p-10 md:p-16 rounded-sm text-center border border-gray-200">
             <div className="space-y-8">
               <h2 className="text-3xl md:text-5xl font-black uppercase leading-none text-brandDark">
                 {lang === 'en' ? "Next Step" : "گام بعدی"}
               </h2>
               <p className="text-gray-500 max-w-xl mx-auto text-lg font-medium">
                 {lang === 'en' 
                   ? "If this process aligns with you, request a discovery session today." 
                   : "اگه این فرایند به نظرت هم‌راستاست، گام بعدی یک گفت‌وگوی آشنایی برای جواب دادن به سؤال‌ها و کشف مسیر توئه."}
               </p>
               <div className="flex flex-col md:flex-row gap-4 justify-center pt-4">
                 <a 
                   href="https://calendar.app.google/NbuyxdkKnzVU9gj17" 
                   target="_blank"
                   rel="noopener noreferrer"
                   className={`inline-flex items-center justify-center gap-3 ${gradientBgClasses} text-white hover:opacity-90 px-8 py-4 text-lg font-black uppercase tracking-widest transition-all shadow-xl hover:shadow-2xl rounded-full`}
                 >
                   <Calendar className="w-5 h-5" />
                   {lang === 'en' ? "Request Discovery Session" : "درخواست جلسه اکتشافی"}
                 </a>
               </div>
             </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default ProcessPage;
